/**
 * Shared types, interfaces, and constants for the WhatToEat app.
 */

// Restaurant interfaces
export interface VisitRecord {
    timestamp: string;
    note: string;
}

export interface Restaurant {
    id: string;
    name_en: string;
    name_zh: string;
    address: string;
    cuisine: string;
    summary: string;
    created_at: string;
    latitude?: number;
    longitude?: number;
    visit_records?: VisitRecord[];
    place_id?: string;
    price_level?: number;
    budget_category?: string;
    top_dishes?: string[];
    keywords?: string[];
    tags?: string[];
    opening_hours?: string;
    want_to_go?: boolean;  // User wants to visit this restaurant
}

// Duplicate check interfaces
export interface DuplicateCheckResponse {
    is_duplicate: boolean;
    existing_restaurant: Restaurant | null;
    matched_by: 'place_id' | 'address' | null;
}

export interface RestaurantPhotosResponse {
    photos: string[];
}

// Travel time interfaces
export type TravelMode = 'DRIVE' | 'WALK' | 'BICYCLE' | 'TRANSIT';

export interface TravelTimeResult {
    restaurant_id: string;
    duration_seconds: number | null;
    duration_text: string;
    distance_meters: number | null;
    distance_text: string;
    status: string;
}

export interface RestaurantsListResponse {
    restaurants: Restaurant[];
}

export interface CuisinesResponse {
    cuisines: string[];
}

export interface TagsResponse {
    tags: string[];
}

// Chat types
export type MessageType = 'user' | 'assistant' | 'system';
export type ViewType = 'chat' | 'restaurants';

// Restaurant sorting types
export type RestaurantSortField = 'name' | 'added' | 'last_visit';
export type SortOrder = 'asc' | 'desc';

// Storage keys
export const STORAGE_KEYS = {
    MODEL: 'whattoeat_model',
    LANGUAGE: 'whattoeat_language',
    USER: 'whattoeat_user',
    AUTH_TOKEN: 'whattoeat_auth_token',
    MAPS_NOTICE_DISMISSED: 'whattoeat_maps_notice_dismissed_v1',
    RECOMMEND_TRAVEL_MODE: 'whattoeat_recommend_travel_mode_v1',
    RECOMMEND_MAX_MINUTES: 'whattoeat_recommend_max_minutes_v1',
    RECOMMEND_LAT: 'whattoeat_recommend_lat_v1',
    RECOMMEND_LNG: 'whattoeat_recommend_lng_v1',
    RECOMMEND_ADDRESS: 'whattoeat_recommend_address_v1',
    RECOMMEND_COUNTRY: 'whattoeat_recommend_country_v1',
    RECOMMEND_MANUAL_LOCATION: 'whattoeat_recommend_manual_location_v1',
    RESTAURANT_SORT_BY: 'whattoeat_restaurant_sort_by_v1',
    RESTAURANT_SORT_ORDER: 'whattoeat_restaurant_sort_order_v1',
    LAST_GEOCODED_LAT: 'whattoeat_last_geocoded_lat_v1',
    LAST_GEOCODED_LNG: 'whattoeat_last_geocoded_lng_v1'
} as const;

// Location refresh interval (1 minute)
export const LOCATION_REFRESH_INTERVAL_MS = 60 * 1000;

// Minimum distance (in meters) to trigger a new reverse geocode call
export const MIN_GEOCODE_DISTANCE_METERS = 100;

// Chat audio data interface
export interface ChatAudioData {
    data: string;
    mime_type: string;
    duration_seconds: number;
}
